<template>
  <div class="desktop-search-bar" :class="{ 'focusable-area': hasFocus }">
    <WCInputGroup class="flex-nowrap">
      <template v-slot:left>
        <WCItemGroupSelector @change="itemGroupSelected" :itemGroups="dropdownItemGroups" />
      </template>

      <div class="wc-search-area" :aria-label="$t('search')">
        <input
          class="form-control wc-search-box"
          ref="searchInput"
          @focusin="focusInSearch(searchInput)"
          @focusout="hideModal"
          :placeholder="$t('search')"
          :value="searchInput"
          @input="handleSearchInputChange"
          @keydown.enter="submitSearch(searchInput)"
          @keydown.down="handleKeyDown()"
          @keydown.up="handleKeyUp()"
          @keydown.esc="handleEscapeKey()"
          role="combobox"
          :aria-label="$t('search')"
          aria-expanded="false"
          v-bind="{ 'aria-expanded': showSuggestions ? 'true' : 'false' }"
          aria-controls="search-listbox"
          :aria-activedescendant="activeSuggestionIndex !== -1 ? 'selected-search-option' : null"
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        <button
          type="button"
          class="wc-search-input-icon"
          :aria-label="$t('search')"
          @click="submitSearch(searchInput)"
          @keydown.enter.prevent="submitSearch(searchInput)"
          @keydown.space.prevent="submitSearch(searchInput)"
        >
          <WCSvgIcon name="search-icon" />
        </button>

        <select
          v-if="showSuggestions && suggestions && suggestions.length && searchInput"
          class="sr-only"
          id="search-listbox"
          :aria-label="$t('suggestionsList')"
          @change="onSuggestionSelectChange"
        >
          <option v-for="(suggestion, index) in suggestions" :key="index" :value="index"
            >{{ suggestion.name
            }}{{
              index === 0 && suggestion.department ? ` - ${suggestion.department}` : ''
            }}</option
          >
        </select>

        <ul
          class="dropdown-menu show"
          v-if="showSuggestions && suggestions && suggestions.length && searchInput"
          aria-hidden="true"
        >
          <li
            class="wrapper"
            v-for="(suggestion, index) in suggestions"
            :key="index"
            @mousemove="activeSuggestionIndex = index"
          >
            <button
              type="button"
              class="wc-suggestion dropdown-item unstyled-btn"
              :class="{ active: activeSuggestionIndex === index }"
              :id="activeSuggestionIndex === index ? 'selected-search-option' : null"
              @mousedown.prevent="submitSearch(suggestion.name)"
              @keydown.enter.prevent="submitSearch(suggestion.name)"
              @keydown.space.prevent="submitSearch(suggestion.name)"
            >
              {{ suggestion.name }}
            </button>

            <button
              type="button"
              @mousedown.stop.prevent="submitSearch(suggestion.name, suggestion.department)"
              @keydown.enter.prevent="submitSearch(suggestion.name, suggestion.department)"
              @keydown.space.prevent="submitSearch(suggestion.name, suggestion.department)"
              class="wc-department dropdown-item unstyled-btn"
              :class="{ active: activeSuggestionIndex === index }"
              v-if="index === 0 && suggestion.department"
            >
              {{ $t('in') }} {{ suggestion.department }}
            </button>
          </li>
        </ul>
      </div>
    </WCInputGroup>
  </div>
</template>

<script>
import WCInputGroup from '@/modules/forms/components/WCInputGroup/WCInputGroup.vue';
import SearchComboBoxMixin from '@/mixins/SearchComboBoxMixin';
import OrderTypesMixin from '@/modules/orderTypes/mixins/OrderTypesMixin';
import WCItemGroupSelector from '@/components/WCItemGroupSelector/WCItemGroupSelector.vue';

export default {
  name: 'WCSearchBarV2',
  components: { WCInputGroup, WCItemGroupSelector },
  mixins: [SearchComboBoxMixin, OrderTypesMixin],
  data() {
    return {
      searchBarResizeObserver: null,
    };
  },
  mounted() {
    this.updateSearchBarWidthVar();
    this.observeSearchBarWidth();
  },
  unmounted() {
    this.disconnectSearchBarWidthObserver();
  },
  computed: {
    dropdownItemGroups() {
      return this.searchableOrderTypes || this.itemGroups || [];
    },
  },

  methods: {
    handleSearchInputChange(event) {
      this.searchInput = event.target.value;
      this.handleSearchInput(event.target.value);
    },

    handleSearchInput(term) {
      this.fetchSuggestions(term);
    },

    focusInput() {
      this.$refs.searchInput?.focus?.();
    },

    onSuggestionSelectChange(event) {
      const index = Number(event.target.value);
      const suggestion = this.suggestions[index];
      if (suggestion) {
        this.submitSearch(suggestion.name);
      }
    },

    updateSearchBarWidthVar() {
      const width = Math.ceil(
        this.$el?.getBoundingClientRect?.().width || this.$el?.offsetWidth || 0,
      );
      if (this.$el) {
        this.$el.style.setProperty('--wc-search-bar-width', `${width}px`);
      }
    },
    observeSearchBarWidth() {
      if (typeof ResizeObserver === 'undefined' || !this.$el) {
        return;
      }
      this.disconnectSearchBarWidthObserver();
      this.searchBarResizeObserver = new ResizeObserver(() => {
        this.updateSearchBarWidthVar();
      });
      this.searchBarResizeObserver.observe(this.$el);
    },

    disconnectSearchBarWidthObserver() {
      if (this.searchBarResizeObserver) {
        this.searchBarResizeObserver.disconnect();
        this.searchBarResizeObserver = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.desktop-search-bar {
  width: 100%;

  :deep(.input-group) {
    align-items: stretch;
    height: 2.25rem;
    border: 0.5px solid var(--dark-lighter-5);
    border-radius: 0.1875rem;
    overflow: visible;
    background-color: var(--white);
  }

  :deep(.input-group-prepend) {
    display: flex;
    margin: 0;
  }

  :deep(.wc-igs-trigger) {
    border-radius: 0.1875rem 0 0 0.1875rem;
  }
}

.wc-search-area {
  position: relative;
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;

  .wc-search-input-icon {
    background: none;
    border: none;
    padding: 0;
    position: absolute;
    left: 0.7rem;
    display: inline-flex;
    color: var(--gray-700, $gray-700);
    cursor: pointer;

    :deep(svg) {
      width: 0.9rem;
      height: 0.9rem;
    }
  }

  .wc-search-box {
    height: 100%;
    border: 0;
    border-radius: 0 0.1875rem 0.1875rem 0;
    box-shadow: none;
    font-size: $font-size-base;
    padding-left: 2rem;

    &::placeholder {
      color: var(--gray-600, $gray-600);
    }

    &.form-control:focus {
      box-shadow: none;
    }
    .form-group-sm .form-control {
      font-size: $font-size-base;
    }
  }

  .dropdown-menu {
    right: 0;
    left: 0;
    top: 100%;
    padding: 0 0 $pad-2;
    margin: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    .dropdown-item {
      &.active,
      &:active,
      &:hover,
      &:focus {
        background-color: var(--secondary-lighter-7, get-color('secondary', 'lighter-7'));
        color: var(--secondary-contrast, get-color-contrast('secondary'));
        cursor: pointer;
      }
    }
  }

  .wc-suggestion {
    display: block;
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    right: 0;
    padding: 0;
    margin: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    padding-left: $pad-2;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .wc-department {
    display: block;
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    font-size: $font-size-xs;
    color: var(--gray);
    padding: 0 0 $pad-2 $pad-3;
    &.active {
      color: inherit;
    }
    &:hover {
      font-weight: 600;
    }
  }
}
</style>
